import React, { useState, useEffect, useMemo } from 'react';
import { Form } from '../../../ui/universe';
import { Button } from 'tdesign-react';
import SafeMathJax from '../../../ui/SafeMathJax';
import { useImageStore, useUIStore, useOcrStore } from '../../../../store';
import { useCollapsibleList } from '../../../../hooks';
import { ResultingCom } from '../components';
import { DEFAULT_MESSAGES } from '../../../../const/constants.js';

/**
 * 折叠图标组件
 */
const CollapseIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.77971 9L7.00005 6.33766L4.22039 9L3.0001 7.83117L7.00005 4L11 7.83117L9.77971 9Z" fill="#32394B" />
  </svg>
);

/**
 * 答案列表组件
 */
function AnswerList({ answers, keyPrefix, onSelect, selectRect }) {
  const { isCollapsed, toggleItem } = useCollapsibleList();

  if (!answers || answers.length === 0) return null;

  if (answers.length === 1) {
    const answer = answers[0];
    const answerKey = `${keyPrefix}_0`;
    return (
      <div>
        {answer.HandwriteInfo && (
          <Form.Item
            label="手写答案"
            onClick={() => onSelect && onSelect(answerKey)}
            style={{ cursor: 'pointer' }}
          >
            <div className="edu_form__item" style={{ color: selectRect === answerKey ? '#4a70ff' : 'inherit' }}>
              <SafeMathJax>{answer.HandwriteInfo}</SafeMathJax>
            </div>
          </Form.Item>
        )}

        {answer.IsCorrect !== undefined && (
          <Form.Item label="答案判断">
            <div className="edu_form__item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className={`edu-correct-icon small ${answer.IsCorrect ? 'correct' : 'error'}`}></span>
              <span>{answer.IsCorrect ? '正确' : '错误'}</span>
            </div>
          </Form.Item>
        )}

        {answer.RightAnswer && (
          <Form.Item label="正确答案">
            <div className="edu_form__item">
              <SafeMathJax>{answer.RightAnswer}</SafeMathJax>
            </div>
          </Form.Item>
        )}

        {answer.KnowledgePoints?.length > 0 && (
          <Form.Item label="知识点">
            <div className="edu_form__item" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {answer.KnowledgePoints.map((point, idx) => (
                <span key={idx} style={{
                  padding: '2px 8px',
                  background: '#f0f5ff',
                  borderRadius: '4px',
                  color: '#4A70ff',
                  fontSize: '12px'
                }}>{point}</span>
              ))}
            </div>
          </Form.Item>
        )}

        {answer.AnswerAnalysis && (
          <Form.Item label="答案分析">
            <div className="edu_form__item">
              <SafeMathJax>{answer.AnswerAnalysis}</SafeMathJax>
            </div>
          </Form.Item>
        )}
      </div>
    );
  }

  return answers.map((answer, answerIdx) => {
    const answerKey = `${keyPrefix}_${answerIdx}`;
    const collKey = `answer_${keyPrefix}_${answerIdx}`;

    const handleTitleClick = () => {
      onSelect && onSelect(answerKey);
      toggleItem(collKey);
    };

    return (
      <div
        key={answerIdx}
        className={`isSub ${isCollapsed(collKey) ? 'isHide' : 'isActive'} edu_container__item edu_container__item--small`}
      >
        <div className="edu_title_container edu_title_container--small" onClick={handleTitleClick}>
          <span className="edu_title__text">答案{answerIdx + 1}</span>
          <span className="edu_title__collaps">
            <CollapseIcon size={12} />
          </span>
        </div>
        <div className="edu_body_container">
          {answer.HandwriteInfo && (
            <Form.Item label="手写答案" onClick={() => onSelect && onSelect(answerKey)} style={{ cursor: 'pointer' }}>
              <div className="edu_form__item" style={{ color: selectRect === answerKey ? '#4a70ff' : 'inherit' }}>
                <SafeMathJax>{answer.HandwriteInfo}</SafeMathJax>
              </div>
            </Form.Item>
          )}
          {answer.IsCorrect !== undefined && (
            <Form.Item label="答案判断">
              <div className="edu_form__item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span className={`edu-correct-icon small ${answer.IsCorrect ? 'correct' : 'error'}`}></span>
                <span>{answer.IsCorrect ? '正确' : '错误'}</span>
              </div>
            </Form.Item>
          )}
          {answer.RightAnswer && (
            <Form.Item label="正确答案">
              <div className="edu_form__item">
                <SafeMathJax>{answer.RightAnswer}</SafeMathJax>
              </div>
            </Form.Item>
          )}
          {answer.KnowledgePoints?.length > 0 && (
            <Form.Item label="知识点">
              <div className="edu_form__item" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {answer.KnowledgePoints.map((point, idx) => (
                  <span key={idx} style={{
                    padding: '2px 8px',
                    background: '#f0f5ff',
                    borderRadius: '4px',
                    color: '#4A70ff',
                    fontSize: '12px'
                  }}>{point}</span>
                ))}
              </div>
            </Form.Item>
          )}
          {answer.AnswerAnalysis && (
            <Form.Item label="答案分析">
              <div className="edu_form__item">
                <SafeMathJax>{answer.AnswerAnalysis}</SafeMathJax>
              </div>
            </Form.Item>
          )}
        </div>
      </div>
    );
  });
}

/**
 * 子题目组件
 */
function SubQuestionList({ subMarkInfos, questionIdx, onSelect, selectRect }) {
  const { isCollapsed, toggleItem } = useCollapsibleList();

  if (!subMarkInfos || subMarkInfos.length === 0) return null;

  if (subMarkInfos.length === 1) {
    const subMark = subMarkInfos[0];
    const subKey = `markInfo${questionIdx}_sub0`;
    return (
      <div style={{ marginTop: '8px' }}>
        {subMark.MarkItemTitle && (
          <Form.Item label="子题题干">
            <div className="edu_form__item">
              <SafeMathJax>{subMark.MarkItemTitle}</SafeMathJax>
            </div>
          </Form.Item>
        )}
        <AnswerList
          answers={subMark.AnswerInfos}
          keyPrefix={subKey}
          onSelect={onSelect}
          selectRect={selectRect}
        />
      </div>
    );
  }

  return subMarkInfos.map((subMark, subIdx) => {
    const subKey = `markInfo${questionIdx}_sub${subIdx}`;
    const collKey = `sub_${questionIdx}_${subIdx}`;

    const handleTitleClick = () => {
      onSelect && onSelect(subKey);
      toggleItem(collKey);
    };

    return (
      <div
        key={subIdx}
        className={`isSub ${isCollapsed(collKey) ? 'isHide' : 'isActive'} edu_container__item`}
        style={{ marginTop: '8px' }}
      >
        <div className="edu_title_container" onClick={handleTitleClick}>
          <span className="edu_title__tips"></span>
          <span className="edu_title__text">子题目{subIdx + 1}</span>
          <span className="edu_title__collaps">
            <CollapseIcon />
          </span>
        </div>
        <div className="edu_body_container">
          {subMark.MarkItemTitle && (
            <Form.Item label="子题题干">
              <div className="edu_form__item">
                <SafeMathJax>{subMark.MarkItemTitle}</SafeMathJax>
              </div>
            </Form.Item>
          )}
          <AnswerList
            answers={subMark.AnswerInfos}
            keyPrefix={subKey}
            onSelect={onSelect}
            selectRect={selectRect}
          />
        </div>
      </div>
    );
  });
}

/**
 * EduMark 渲染器 - 教育批改结果
 */
function EduMarkRenderer({ questionInfo: propQuestionInfo, markInfos: propMarkInfos, onSelect }) {
  const selectRect = useImageStore(state => state.selectRectKey);
  const isMarkLoading = useUIStore(state => state.isMarkLoading);
  const eduMarkData = useOcrStore(state => state.eduMarkData);

  const questionInfo = propQuestionInfo ?? (eduMarkData?.data?.Response?.QuestionInfo?.[0]?.ResultList || eduMarkData?.QuestionInfo?.[0]?.ResultList || []);
  const markInfos = propMarkInfos ?? (eduMarkData?.data?.Response?.MarkInfos || eduMarkData?.MarkInfos || []);
  
  // 判断是否有批改结果数据（用于控制 loading 显示）
  const hasMarkInfos = markInfos && markInfos.length > 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [internalSelect, setInternalSelect] = useState(false);

  const questionsWithMark = useMemo(() => {
    if (questionInfo && questionInfo.length > 0) {
      return questionInfo.map((question, index) => ({
        ...question,
        markData: markInfos[index] || null
      }));
    }

    if (markInfos && markInfos.length > 0) {
      return markInfos.map((markInfo, index) => ({
        QuestionIndex: index,
        QuestionTitle: markInfo.MarkItemTitle || `题目${index + 1}`,
        markData: markInfo
      }));
    }

    return [];
  }, [questionInfo, markInfos]);

  const currentQuestion = questionsWithMark[currentIndex] || {};
  const currentMark = currentQuestion.markData || {};
  const { MarkItemTitle, AnswerInfos = [], MarkInfos: SubMarkInfos = [] } = currentMark;

  const handleInternalSelect = (key) => {
    setInternalSelect(true);
    onSelect && onSelect(key);
  };

  const hasMarkData = (index) => questionsWithMark[index]?.markData !== null;

  useEffect(() => {
    if (questionsWithMark.length > 0) {
      handleInternalSelect('Question01');
    }
  }, [questionsWithMark.length > 0]);

    // 监听selectRect变化，切换到对应的题目（仅处理外部变更）
    useEffect(() => {
      if (!selectRect) return;
      console.log('selectRect',selectRect)
      
      // 如果是内部触发的变更，跳过处理并重置标记
      if (internalSelect) {
        setInternalSelect(false);
        return;
      }
      
      // 解析selectRect，提取题目索引
      // 支持格式：Question01, Question111, markInfo0_0, markInfo0_sub0_0 等
      let questionIdx = null;
      
      if (selectRect.startsWith('Question')) {
        // Question01 -> index 0, Question11 -> index 1, Question111 -> index 11
        // 格式为 Question + index + 1（固定后缀），去掉最后一个字符后取数字
        const withoutSuffix = selectRect.slice(8, -1); // 去掉 "Question" 和最后的 "1"
        if (withoutSuffix && /^\d+$/.test(withoutSuffix)) {
          questionIdx = parseInt(withoutSuffix, 10);
        }
      } else if (selectRect.startsWith('markInfo')) {
        // markInfo0_0, markInfo0_sub0_0 等
        const match = selectRect.match(/markInfo(\d+)/);
        if (match) {
          questionIdx = parseInt(match[1], 10);
        }
      }
      
      // 如果解析出有效的题目索引，且与当前不同，则切换
      if (questionIdx !== null && questionIdx > -1 &&  questionIdx !== currentIndex && hasMarkData(questionIdx)) {
        setCurrentIndex(questionIdx);
      }
    }, [selectRect]);

  const { totalQuestions, wrongQuestionCount, wrongSubQuestionCount } = useMemo(() => {
    const total = questionsWithMark.filter(q => q.markData).length;
    let wrongQuestion = 0;
    let wrongSubQuestion = 0;

    const countWrongAnswers = (markInfo) => {
      let count = 0;
      const answers = markInfo.AnswerInfos || [];
      answers.forEach(a => {
        if (a.IsCorrect === false) count++;
      });
      const subMarkInfos = markInfo.MarkInfos || [];
      subMarkInfos.forEach(subMark => {
        count += countWrongAnswers(subMark);
      });
      return count;
    };

    questionsWithMark.forEach(question => {
      if (!question.markData) return;
      const wrongCount = countWrongAnswers(question.markData);
      if (wrongCount > 0) wrongQuestion++;
      wrongSubQuestion += wrongCount;
    });

    return { totalQuestions: total, wrongQuestionCount: wrongQuestion, wrongSubQuestionCount: wrongSubQuestion };
  }, [questionsWithMark]);

  return (
    <div className="edu_container">
      {!isMarkLoading && totalQuestions > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'linear-gradient(93.26deg, #EBF2FF 0.79%, #F7FAFF 21.4%)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1C4.13 1 1 4.13 1 8C1 11.87 4.13 15 8 15C11.87 15 15 11.87 15 8C15 4.13 11.87 1 8 1ZM8 13.5C4.97 13.5 2.5 11.03 2.5 8C2.5 4.97 4.97 2.5 8 2.5C11.03 2.5 13.5 4.97 13.5 8C13.5 11.03 11.03 13.5 8 13.5Z" fill="#1890ff"/>
            <path d="M8 11C8.55228 11 9 10.5523 9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11Z" fill="#1890ff"/>
            <path d="M7.25 5H8.75V8H7.25V5Z" fill="#1890ff"/>
          </svg>
          <span style={{ fontSize: '14px', color: '#333', fontWeight: 500 }}>
            总共解析 <strong style={{ color: '#4A70ff' }}>{totalQuestions}</strong> 道大题
            {wrongQuestionCount > 0 ? (
              <>，其中有 <strong style={{ color: '#4A70ff' }}>{wrongSubQuestionCount}</strong> 道小题错误</>
            ) : (
              <>，全部正确</>
            )}
          </span>
        </div>
      )}

      <div className="edu_mark_tabs" style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {questionsWithMark.map((_, index) => {
          const hasMark = hasMarkData(index);
          return (
            <Button
              key={index}
              variant={currentIndex === index ? 'base' : 'outline'}
              theme={currentIndex === index ? 'primary' : 'default'}
              disabled={!hasMark}
              onClick={() => {
                if (hasMark) {
                  setCurrentIndex(index);
                  handleInternalSelect(`Question${index}1`);
                }
              }}
              style={{ opacity: hasMark ? 1 : 0.5, cursor: hasMark ? 'pointer' : 'not-allowed' }}
            >
              题目{index + 1}
            </Button>
          );
        })}
      </div>

      {isMarkLoading && !hasMarkInfos && (
        <ResultingCom msg={DEFAULT_MESSAGES.RESULTING} />
      )}

      {hasMarkData(currentIndex) && (
        <div className="edu_body_container">
          {MarkItemTitle && (
            <Form.Item
              label="大题题干"
              onClick={() => handleInternalSelect(`Question${currentIndex}1`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="edu_form__item" style={{ color: selectRect === `Question${currentIndex}1` ? '#4a70ff' : 'inherit' }}>
                <SafeMathJax>{MarkItemTitle}</SafeMathJax>
              </div>
            </Form.Item>
          )}

          <AnswerList
            answers={AnswerInfos}
            keyPrefix={`markInfo${currentIndex}`}
            onSelect={handleInternalSelect}
            selectRect={selectRect}
          />

          <SubQuestionList
            subMarkInfos={SubMarkInfos}
            questionIdx={currentIndex}
            onSelect={handleInternalSelect}
            selectRect={selectRect}
          />
        </div>
      )}
    </div>
  );
}

export default EduMarkRenderer;
